import React, { useState } from "react";
import styled from "@emotion/styled";
import { TableCell } from "~/table";
import { Dialog, DialogContent } from "~/components/dialog";
import ClickSwallower from "~/click-swallower";
import Button from "~/components/button";
import { ImageInfo } from "~/file-loading/useLoadFile";
import {
  DownloadFileProps,
  useDownloadFile,
} from "~/file-loading/useDownloadFile";
import { InlineProgress } from "~/components/inline-progress";
import { useLazyLoadImage } from "./useLazyLoadImage";

const ActionsCell = styled(TableCell)`
  padding-right: 0;
  display: flex;
  justify-content: right;
  button.mdc-button {
    min-width: unset;
    width: 40px;
    padding: 0;
    margin: 0;
  }
`;

const StyledDialog = styled(Dialog)`
  min-width: 800px;
  max-width: unset;

  .mdc-dialog__container {
    min-width: 800px;
    max-width: unset;

    .mdc-dialog__surface {
      min-width: 800px;
      max-width: unset;
    }
  }
`;

const PreviewContent = styled.img`
  width: 800px;
  min-height: 640px;
  max-height: 740px;
  object-fit: contain;
  overflow: hidden;
`;

const PreviewEmbed = styled.embed`
  width: 800px;
  min-height: 640px;
  max-height: 740px;
  object-fit: contain;
  overflow: hidden;
`;

const LoadingContainer = styled.div`
  text-align: center;
`;

const CloseButtonContainer = styled.div`
  text-align: right;
`;

export type ImagePreviewActionsProps = {
  imageUrl: string;
  filename: string;
  mockDownloader?: (args: DownloadFileProps) => void;
};

export const ImagePreviewActions: React.FC<ImagePreviewActionsProps> = ({
  imageUrl,
  filename,
  mockDownloader,
}) => {
  const download = useDownloadFile();
  const downloadImage = mockDownloader ?? download;
  const getImage = useLazyLoadImage(imageUrl);

  const [dialogState, setDialogState] = useState<{
    open: boolean;
    image: ImageInfo | null;
  }>({ open: false, image: null });

  const handleDownload = async () => {
    const imageInfo = await getImage();
    downloadImage({ linkHref: imageInfo.url, filename });
  };

  const previewImage = async () => {
    setDialogState((st) => ({ ...st, open: true }));
    const imageInfo = await getImage();
    setDialogState((st) => ({ ...st, image: imageInfo }));
  };

  const isPdf = dialogState.image?.type === "application/pdf";
  const url = dialogState.image?.url;

  return (
    <ClickSwallower>
      <ActionsCell>
        <Button title="preview" icon="preview" onClick={previewImage} />
        <Button
          title="download"
          icon="file_download"
          onClick={handleDownload}
        />
      </ActionsCell>

      <StyledDialog
        open={dialogState.open}
        onClose={() => setDialogState((st) => ({ ...st, open: false }))}
        data-open={dialogState.open}
        portalize
      >
        <DialogContent>
          {!dialogState.image && (
            <LoadingContainer>
              <InlineProgress />
            </LoadingContainer>
          )}
          <>
            {!isPdf && url && <PreviewContent src={url} alt="" />}
            {isPdf && url && <PreviewEmbed src={url} />}
          </>
          <CloseButtonContainer>
            <Button
              onClick={() => setDialogState((st) => ({ ...st, open: false }))}
            >
              Close
            </Button>
          </CloseButtonContainer>
        </DialogContent>
      </StyledDialog>
    </ClickSwallower>
  );
};
